import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Routes from 'Routes';
import { Trans } from 'react-i18next';
import moment from 'moment-timezone';
import {
  onDemandWebinarSortFn,
  selectAllRelevantTracks,
  Webinar,
  webinarSortFn,
  WebinarType,
} from 'models/webinars';
import { selectLocale, selectMomentLocalizer } from 'models/localization';

const SessionsContainer = styled.div``;

const SessionItemContainer = styled(Link)<{ $borderColor: string, $backgroundColor: string }>`
  display: block;
  border: ${({ $borderColor }) => `2px solid ${$borderColor}`};
  box-sizing: border-box;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  text-decoration: none;
  position: relative;
  &:before {
    pointer-events: none;
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    opacity: 0.2;
    background: ${({ $backgroundColor }) => $backgroundColor};
  }
`;

const SessionTitle = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: #17181E;
`;

const SessionTime = styled.div`
  font-size: 14px;
  line-height: 17px;
  color: #999;
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
`;

const DateContainer = styled.div`
  margin-right: 4px;
`;

const TimeZoneContainer = styled.div`
  color: #999;
  margin-right: 4px;
`;

const WebinarTypeValueMap: Record<WebinarType, number> = {
  [WebinarType.Live]: 0,
  [WebinarType.OnDemand]: 1,
};
export interface WebinarSpeakerSessionsProps {
  webinars: Webinar[];
}

const WebinarSpeakerSessions: FC<WebinarSpeakerSessionsProps> = ({ webinars }) => {
  const locale = useSelector(selectLocale);
  const momentLocalizer = useSelector(selectMomentLocalizer);
  const trackIds = useSelector(selectAllRelevantTracks()).map((track) => track.id);

  const getTrackColor = useCallback((track, key) => {
    const defaultColor = {
      border: '#DBDEE2',
      background: '#fff',
    };

    if (!trackIds.includes(track?.id)) { // If not in relevant track, then show default
      return defaultColor[key];
    }

    // only show track color in session with multiple track
    return track?.color || defaultColor[key];
  }, [trackIds]);

  const processedWebinars = useMemo(() => webinars
    .sort((prevWebinar, nextWebinar) => {
      const prevKindValue = WebinarTypeValueMap[prevWebinar.kind];
      const nextKindValue = WebinarTypeValueMap[nextWebinar.kind];

      if (prevKindValue - nextKindValue !== 0) return prevKindValue - nextKindValue;

      // prev and next Webinar have the same WebinarType
      if ([prevWebinar, nextWebinar].every((webinar) => webinar.kind === WebinarType.Live)) {
        const liveSortFn = webinarSortFn(locale);
        return liveSortFn(prevWebinar, nextWebinar);
      }

      if ([prevWebinar, nextWebinar].every((webinar) => webinar.kind === WebinarType.OnDemand)) {
        return onDemandWebinarSortFn(prevWebinar, nextWebinar);
      }

      return 0;
    })
    .map((webinar) => {
      const localizedStartAt = momentLocalizer(webinar.startAt);
      return {
        ...webinar,
        unixStart: localizedStartAt.unix(),
        startAt: webinar.startAt,
        durationInMins: Math.abs(moment.duration(moment(webinar.startAt).diff(webinar.endAt)).asMinutes()),
        startTime: localizedStartAt.format('h:mmA'),
        endTime: momentLocalizer(webinar.endAt).format('h:mmA'),
        timezoneString: `GMT${localizedStartAt.format('Z')}`,
        date: localizedStartAt.format('ddd, MMM DD'),
        borderColor: getTrackColor(webinar?.track, 'border'),
        backgroundColor: getTrackColor(webinar?.track, 'background'),
      };
    }), [locale, webinars, getTrackColor, momentLocalizer]);

  return (
    <SessionsContainer>
      {processedWebinars.map(({
        id,
        borderColor,
        backgroundColor,
        translations,
        startAt,
        startTime,
        endTime,
        durationInMins,
        timezoneString,
        date,
      }) => (
        <SessionItemContainer
          key={`SpeakerWebinar-${id}`}
          $borderColor={borderColor}
          $backgroundColor={backgroundColor}
          to={Routes.webinarSession(id)}
        >
          <SessionTitle>{translations[locale]?.title}</SessionTitle>
          {startAt && (
            <SessionTime>
              <Trans
                defaults={`${startTime} - ${endTime} (${durationInMins} mins) (${timezoneString})`}
                i18nKey="webinar.time_format"
                components={{ b: <DateContainer />, i: <TimeZoneContainer /> }}
                values={{
                  startTime, endTime, duration: durationInMins, timezone: timezoneString,
                }}
              />
              {date}
            </SessionTime>
          )}
        </SessionItemContainer>
      ))}
    </SessionsContainer>
  );
};

export default WebinarSpeakerSessions;
